<template>
  <div v-loading="!isLoaded">
    <tf-table
      v-if="isLoaded"
      v-test:table="'census files'"
      v-bind="{
        parentalControls: true,
        tableData,
        tableMeta,
      }"
      @changeSortCriteria="$emit('changeSortCriteria', $event)"
    >
      <template #title>
        Census files
      </template>
      <tf-button
        v-if="tableData.length"
        slot="ctaHeader"
        v-test:button="'add census files'"
        type="primary"
        icon="plus"
        size="text-icon"
        @click="onAddCensusFiles"
      >
        Add census files
      </tf-button>
      <template
        v-if="error"
        #error
      >
        {{ error }}
      </template>
      <source-table-row
        :key="rowData.id"
        slot="parentRow"
        slot-scope="{ rowData }"
        :source="rowData"
        :show-date="true"
        @delete="deleteSource"
        @save="setSourceProp({
          id: $event.id,
          prop: 'name',
          value: $event.name,
        })"
      />
      <empty-table-cell slot="empty">
        Census files haven’t been added yet.
        <tf-button
          slot="cta"
          type="secondary"
          icon="plus"
          size="medium"
          @click="onAddCensusFiles"
        >
          Add census files
        </tf-button>
      </empty-table-cell>
    </tf-table>
  </div>
</template>

<script>
  // vuex
  import {
    mapActions,
    mapMutations,
    mapState,
  } from 'vuex';
  // services
  import EmployerService from '@/services/employer';
  // components
  import SourceTableRow from '@/components/SourceTableRow';
  import EmptyTableCell from '@/components/EmptyTableCell';
  /**
   * Census Files Table
   * This is an extension of the EmployerTable component in which the table gets the Census Files data from an API that is controlled locally, but sorting is controlled by the parent.
   *
   * @vuedoc
   * @exports Administration/Tables/CensusSources
   * @category Components
   */
  export default {
    name: 'TableCensusSources',
    components: { EmptyTableCell, SourceTableRow },
    props: {
      clientId: {
        type: [String, Number],
        required: true,
      },
      sortDirection: {
        type: String,
        default: 'asc',
      },
      sortProp: {
        type: String,
        default: 'name',
      },
    },
    data: () => ({
      error: null,
    }),
    computed: {
      ...mapState({
        isLoaded: (state) => state.employer.censusSources.isLoaded,
        tableData: (state) => state.employer.censusSources.sources,
      }),
      /**
       * Return an object to pass to the 'tableMeta' tf-table. The sort parameters are computed based on the instance props.
       *
       * @returns {object}
       */
      tableMeta() {
        return {
          columns: [
            {
              format: 'name',
              label: 'File name',
              prop: 'name',
              sortable: true,
            },
            {
              format: 'date',
              label: 'Date added',
              prop: 'created_at',
              sortable: true,
              width: '60',
            },
            {
              format: 'custom',
              label: null,
              prop: null,
              width: '30',
            },
          ],
          sort: {
            direction: this.sortDirection,
            prop: this.sortProp,
          },
        };
      },
    },
    created() {
      if (this.isLoaded) {
        return;
      }

      this.setSourcesIsLoaded();

      EmployerService
        .getCensusSources(this.clientId)
        .then((data) => {
          this.setSources(data.census_sources);
        })
        .catch(() => {
          this.error = 'There was an error getting your Census Files.';
        })
        .finally(() => {
          this.setSourcesIsLoaded(true);
        });
    },
    beforeDestroy() {
      this.resetSources();
    },
    methods: {
      ...mapMutations({
        deleteSource: 'DELETE_EMPLOYER_SOURCE',
        setSourceProp: 'SET_EMPLOYER_SOURCE_PROP',
        setSources: 'SET_EMPLOYER_SOURCES',
        setSourcesIsLoaded: 'SET_SOURCES_IS_LOADED',
      }),
      ...mapActions(['resetSources']),
      /**
       * Start Add census files modal flow and track in analytics.
       */
      onAddCensusFiles() {
        this.$emit('clickAddCensus');

        this.$TF.Analytics.trackEvent('Add census files');
      },
    },
  };
</script>

<style scoped lang="scss">
  ::v-deep {
    .el-form {
      &-item {
        margin-bottom: 0;
      }
    }
  }
</style>
